import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, ShieldCheck, Coins, DollarSign, TrendingUp, Hexagon, ArrowUpRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

export default function AdminDashboard() {
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-zinc-500">Failed to load dashboard</p>;

  const metricCards = [
    { label: 'Total Users', value: data.total_users, icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    { label: 'Links Protected', value: data.total_links, icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    { label: 'Points Distributed', value: data.total_points_distributed.toLocaleString(), icon: Coins, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    { label: 'Est. Revenue Saved', value: `$${data.estimated_revenue_saved.toLocaleString()}`, icon: DollarSign, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' },
    { label: 'Unique Creators', value: data.unique_creators, icon: Hexagon, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
          <p className="text-zinc-400 mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="font-mono font-bold" style={{ color: p.color }}>
              {p.name}: {p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-8">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {metricCards.map((m, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className={`glass-card rounded-xl p-5 border ${m.border} hover:scale-[1.02] transition-transform`}
            data-testid={`admin-metric-${i}`}
          >
            <div className={`w-10 h-10 rounded-xl ${m.bg} flex items-center justify-center mb-3`}>
              <m.icon className={`w-5 h-5 ${m.color}`} />
            </div>
            <p className="font-heading font-black text-2xl text-white">{m.value}</p>
            <p className="text-xs text-zinc-500 mt-1">{m.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">User Growth (30 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.user_growth}>
              <defs>
                <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#71717A' }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#71717A' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="users" stroke="#3B82F6" fill="url(#userGrad)" strokeWidth={2} name="New Users" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Link Growth */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">Links Protected (30 days)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={data.link_growth}>
              <defs>
                <linearGradient id="linkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#71717A' }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#71717A' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="links" stroke="#F59E0B" fill="url(#linkGrad)" strokeWidth={2} name="Links Protected" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Row: Top Networks + Top Creators */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Networks */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">Top Affiliate Networks</h3>
          <div className="space-y-3">
            {data.top_networks.map((n, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`top-network-${i}`}>
                <span className="text-xs text-zinc-600 font-mono w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-300 truncate">{n.network}</p>
                </div>
                <span className="text-sm font-mono font-bold text-amber-500">{n.count}</span>
              </div>
            ))}
            {data.top_networks.length === 0 && <p className="text-xs text-zinc-600">No data yet</p>}
          </div>
        </div>

        {/* Top Domains */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">Top Protected Domains</h3>
          <div className="space-y-3">
            {data.top_domains.map((d, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`top-domain-admin-${i}`}>
                <span className="text-xs text-zinc-600 font-mono w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-300 truncate">{d.domain}</p>
                </div>
                <span className="text-sm font-mono font-bold text-emerald-400">{d.count}</span>
              </div>
            ))}
            {data.top_domains.length === 0 && <p className="text-xs text-zinc-600">No data yet</p>}
          </div>
        </div>

        {/* Top Creators */}
        <div className="glass-card rounded-xl p-6">
          <h3 className="font-heading font-bold text-sm text-white mb-4">Top Creators</h3>
          <div className="space-y-3">
            {data.top_creators.map((c, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`top-creator-${i}`}>
                <span className="text-xs text-zinc-600 font-mono w-5">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-300 truncate">{c.creator}</p>
                </div>
                <span className="text-sm font-mono font-bold text-purple-400">{c.count}</span>
              </div>
            ))}
            {data.top_creators.length === 0 && <p className="text-xs text-zinc-600">No data yet</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
